import { ICellRendererParams } from "ag-grid-community";

import { AuthUserContextType } from "../../context/AuthUser";
import {
  OrderDetailFormRowValues,
  OrderDetailFormValues,
} from "../../model/form/OrderDetailForm";
import { OrderStatus, ProductCodeConst } from "../../util/Constant";

/** 表示・編集可否判定に必要な画面状態 */
type OrderDetailPermissionContext = {
  inpOrder: OrderDetailFormValues;
  inpDetails: OrderDetailFormRowValues[];
  readOnly: boolean;
  auth: AuthUserContextType;
  fromQuotationDetail?: boolean;
  /** キャンセル金額（税込み）取得 */
  getCancelTotalPrice: () => number;
};

export type OrderDetailPermission = ReturnType<
  typeof createOrderDetailPermission
>;

/**
 * 注文詳細の表示・編集可否判定
 * （ステータス × ユーザ種別 × 各フラグ による判定を一箇所に集約）
 * 毎レンダリングで呼び出し、その時点の state に基づく判定関数群を返す。
 * 判定関数は state を更新せず、引数で受け取った値だけを参照する。
 */
export function createOrderDetailPermission(ctx: OrderDetailPermissionContext) {
  const {
    inpOrder,
    inpDetails,
    readOnly,
    auth,
    fromQuotationDetail,
    getCancelTotalPrice,
  } = ctx;

  // 添付ファイル使用可否判定（顧客のみ）
  function editableFileUpload() {
    if (readOnly && !fromQuotationDetail) {
      // 読み取り専用モードで注文変更でない時は編集不可
      return false;
    }
    if (inpOrder.isChangeOrder) {
      // 注文変更モードの時は編集不可
      return false;
    }
    if (auth.user().bemacFlg) {
      // 顧客のみ編集可能
      return false;
    }
    if (inpOrder.statusClassValue === OrderStatus.ARRANGING) {
      // 部品手配中は編集不可
      return false;
    }

    // その他の場合編集可能
    return true;
  }

  // 添付ファイル（送り状）使用可否判定
  function editableShippingFileUpload() {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (!auth.user().bemacFlg) {
      // 顧客は不可
      return false;
    }
    if (
      inpOrder.statusClassValue === OrderStatus.ORDERED ||
      inpOrder.statusClassValue === OrderStatus.ARRANGING
    ) {
      // 「注文中」または「手配中」の場合編集可能
      return true;
    }

    // その他の場合不可
    return false;
  }

  // お客様備考編集可否判定
  function editableUserComment() {
    if (auth.user().bemacFlg) {
      // BEMACは編集不可
      return false;
    }
    if (inpOrder.isChangeOrder) {
      // 注文変更モードの時は編集不可
      return false;
    }

    //部品手配中かつログインユーザ＝注文依頼者なら編集可(readOnlyより優先度高)
    if (
      OrderStatus.ARRANGING === inpOrder.statusClassValue &&
      inpOrder.orderUserId &&
      auth.user().userId === inpOrder.orderUserId
    ) {
      return true;
    }

    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    //部品手配中は編集可(readOnlyより優先度高)
    if (OrderStatus.ARRANGING === inpOrder.statusClassValue) {
      return true;
    }

    // 作成中、注文中、否認は編集可
    return [
      OrderStatus.DRAFT,
      OrderStatus.ORDERED,
      OrderStatus.REQUESTED_CHANGE,
    ].includes(inpOrder.statusClassValue);
  }

  // お客様備考注釈表示条件(注文取消)
  function showsUserCommentCancel() {
    if (auth.user().bemacFlg) {
      // BEMACは非表示
      return false;
    }

    // 注文中、部品手配中の場合表示
    if (
      inpOrder.statusClassValue === OrderStatus.ORDERED ||
      inpOrder.statusClassValue === OrderStatus.ARRANGING
    ) {
      return true;
    }
    // その他は非表示
    return false;
  }

  // お客様備考注釈表示条件(注文停止)
  function showsUserCommentDeniedChange() {
    if (auth.user().bemacFlg) {
      // BEMACは非表示
      return false;
    }

    // 否認(注文変更依頼後)の場合表示
    if (inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE) {
      return true;
    }
    // その他は非表示
    return false;
  }

  // 注文回答コメント編集可否判定
  function editableBemacReplyComment() {
    if (!auth.user().bemacFlg) {
      // 顧客は編集不可
      return false;
    }

    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    //「注文中」→「取消依頼中」の場合編集可
    if (
      inpOrder.statusClassValue === OrderStatus.REQUESTED_CANCEL &&
      !inpOrder.replyDateTime
    ) {
      return true;
    }

    // 注文中のみ編集可
    return inpOrder.statusClassValue === OrderStatus.ORDERED;
  }

  // フリーメモ編集可否判定
  function editableBemacFreeComment() {
    if (!auth.user().bemacFlg) {
      // 顧客は編集不可
      return false;
    }

    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // 否認、停止、変更、取消時は編集不可
    return ![
      OrderStatus.REQUESTED_CHANGE,
      OrderStatus.CHANGED,
      OrderStatus.DENIED_CHANGE,
      OrderStatus.STOPPED_SHIPPING,
    ].includes(inpOrder.statusClassValue);
  }

  // 出荷完了コメント編集可否判定
  function editableBemacShippingComment() {
    if (!auth.user().bemacFlg) {
      // 顧客は編集不可
      return false;
    }
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    //注文取消依頼中かつ回答日時がnullでなければ編集可(=部品手配中経由の注文取消依頼)
    if (
      inpOrder.statusClassValue === OrderStatus.REQUESTED_CANCEL &&
      inpOrder.replyDateTime
    ) {
      return true;
    }

    // 部品手配中、完了のみ編集可
    return (
      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
      inpOrder.statusClassValue === OrderStatus.COMPLETED
    );
  }

  // 行追加可否
  function canAddNewRow() {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (inpOrder.isChangeOrder) {
      // 注文変更モードは編集不可
      return false;
    }

    // BEMAC・注文中、部品手配中、完了は可
    if (
      auth.user().bemacFlg &&
      (inpOrder.statusClassValue === OrderStatus.ORDERED ||
        inpOrder.statusClassValue === OrderStatus.ARRANGING ||
        inpOrder.statusClassValue === OrderStatus.COMPLETED)
    ) {
      return true;
    }

    // 顧客・作成中は可
    if (
      !auth.user().bemacFlg &&
      inpOrder.statusClassValue === OrderStatus.DRAFT
    ) {
      return true;
    }

    return false;
  }

  // 全明細出荷済みか（更新ボタンで完了へ遷移できるか）
  function canChangeComplete() {
    // 明細のうち一つでも出荷済みでなければfalse
    for (const row of inpDetails) {
      if (!row.confirmFlg && row.isDbRow) {
        return false;
      }
    }
    return true;
  }

  // 行ドラッグ可否判定
  function canDragRow() {
    if (
      readOnly ||
      (!auth.user().bemacFlg && OrderStatus.DRAFT !== inpOrder.statusClassValue)
    ) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (inpOrder.isChangeOrder) {
      // 注文変更モードの時は編集不可
      return false;
    }
    return true;
  }

  // 技師一般ユーザかどうか
  function isGeneralEngineer() {
    return auth.isEngineer() && !auth.user().adminFlg;
  }

  // 仮注文金額エリア表示判定
  function showsTempPrice() {
    // 顧客ユーザかつ未承認
    return (
      !auth.user().bemacFlg &&
      !fromQuotationDetail &&
      !inpOrder.quotationNo &&
      !inpOrder.replyDateTime &&
      !inpOrder.replyDiscountPrice &&
      !inpOrder.replyTax &&
      inpOrder.statusClassValue !== OrderStatus.REQUESTED_CHANGE
    );
  }

  // セルの文字を赤くする判定
  function showsChangeColor(
    row: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    if (inpOrder.statusClassValue !== OrderStatus.REQUESTED_CHANGE) {
      // 否認以外は適用しない
      return false;
    }

    const data = row.data!;
    const isBemacRow = data.quantity === 0;
    const hasChangedCost = data.replyUnitCost !== data.unitCost;
    const hasChangedQuantity = data.replyQuantity !== data.quantity;
    const hasChangedSubTotal = data.replyDetailSubTotal !== data.detailSubTotal;

    // 依頼と回答で値が異なるものを赤文字にする
    switch (row.colDef?.field) {
      case "replyUnitCost":
        return isBemacRow || hasChangedCost;
      case "replyQuantity":
        return isBemacRow || hasChangedQuantity;
      case "replyDetailSubTotal":
        return isBemacRow || hasChangedSubTotal;
      default:
        return isBemacRow;
    }
  }

  // 列編集可否判定
  function editableColumn(row: ICellRendererParams<OrderDetailFormRowValues>) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (inpOrder.isChangeOrder) {
      // 注文変更モードの時は編集不可
      return false;
    }

    const data = row.data!;

    // 品目コード入力済みの場合は単位入力不可
    if (data.productCode && row.colDef?.field === "unit") {
      return false;
    }

    // 依頼数量はBEMACまたは品目コード=「---」の場合は入力不可
    if (row.colDef?.field === "quantity") {
      if (
        auth.user().bemacFlg ||
        data?.productCode === ProductCodeConst.BLANK
      ) {
        return false;
      }
    }

    if (inpOrder.statusClassValue === OrderStatus.DRAFT) {
      // 作成中は編集可
      return true;
    } else if (
      inpOrder.statusClassValue === OrderStatus.ORDERED ||
      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
      inpOrder.statusClassValue === OrderStatus.COMPLETED
    ) {
      // 注文中、部品手配中、完了は自分が追加した行のみ編集可
      // （注文中、部品手配中、完了ならDB追加済み＝顧客が追加した行とみなせる）
      return auth.user().bemacFlg && !data.isDbRow;
    }

    // その他は編集不可
    return false;
  }

  // BEMAC回答列の編集可否判定
  function editableReplyColumn(
    rowData: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // 顧客は編集不可
    if (!auth.user().bemacFlg) {
      return false;
    }

    //経費行の場合は回答数量のみ編集不可
    if (
      rowData.data?.productCode === ProductCodeConst.EXPENSES &&
      rowData.colDef?.field === "replyQuantity"
    ) {
      return false;
    }

    //品目コード=「---」の場合は編集不可
    if (rowData.data?.productCode === ProductCodeConst.BLANK) {
      return false;
    }

    // 経費・送料は「注文中」「手配中」で未確定行の編集可
    if (
      (rowData.data?.isExpenses || rowData.data?.isDeliveryCost) &&
      (inpOrder.statusClassValue === OrderStatus.ORDERED ||
        inpOrder.statusClassValue === OrderStatus.ARRANGING) &&
      !rowData.data.confirmFlg
    ) {
      return true;
    }

    // 「注文中」、「部品手配中」、「完了」は編集可
    if (
      inpOrder.statusClassValue === OrderStatus.ORDERED ||
      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
      inpOrder.statusClassValue === OrderStatus.COMPLETED
    ) {
      return true;
    }

    // その他は編集不可
    return false;
  }

  // R3関連項目入力可能判定
  function editableR3Values(
    rowData?: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    if ("---" === rowData?.data?.productCode) {
      return false;
    }
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // 顧客は編集不可
    if (!auth.user().bemacFlg) {
      return false;
    }

    // 自身がBEMACユーザで「部品手配中」は編集可
    if (inpOrder.statusClassValue === OrderStatus.ARRANGING) {
      return true;
    }

    // その他は編集不可
    return false;
  }

  // 出荷納期表示判定
  function visibleReplyDeliveryDate() {
    if (auth.user().bemacFlg) {
      // BEMACは常に表示
      return true;
    } else {
      // 顧客は作成中、注文中は非表示
      if (
        inpOrder.statusClassValue === OrderStatus.DRAFT ||
        inpOrder.statusClassValue === OrderStatus.ORDERED
      ) {
        return false;
      } else {
        return true;
      }
    }
  }

  // 出荷納期入力可能判定
  function editableReplyDeliveryDate() {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // 顧客は編集不可
    if (!auth.user().bemacFlg) {
      return false;
    }

    // 自身がBEMACユーザで「注文中」は編集可
    if (inpOrder.statusClassValue === OrderStatus.ORDERED) {
      return true;
    }

    // その他は編集不可
    return false;
  }

  // グリッド内の削除ボタン使用可否
  function canDeleteRow(
    rowData: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    // 最終行または技師派遣フラグON時の経費行の場合は削除不可
    if (
      rowData.data?.orderDetailNo === inpDetails.length ||
      (rowData.data?.productCode === ProductCodeConst.EXPENSES &&
        inpOrder.dispatchFlg)
    ) {
      return false;
    }
    return editableColumn(rowData);
  }

  // "BEMAC注文回答内容"表示条件
  function showsReplyColumns() {
    // BEMACユーザは常に表示
    if (auth.user().bemacFlg) {
      return true;
    }

    // 顧客の作成中は非表示
    if (inpOrder.statusClassValue === OrderStatus.DRAFT) {
      return false;
    }
    // BEMACが承認するまでは非表示
    if (inpOrder.statusClassValue === OrderStatus.ORDERED) {
      return false;
    }
    // その他は表示
    return true;
  }

  // 出荷後キャンセル内容「数量」列入力可能判定
  function editableCancelQuantity(
    rowData: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    if ("---" === rowData.data?.productCode) {
      return false;
    }

    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // 顧客は編集不可
    if (!auth.user().bemacFlg) {
      return false;
    }

    // 出荷してなければ編集不可
    if (!rowData.data?.confirmDateTime) {
      return false;
    }

    // 自身がBEMACユーザで「部品手配中」「完了」は編集可
    if (
      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
      inpOrder.statusClassValue === OrderStatus.COMPLETED
    ) {
      return true;
    }

    // その他は編集不可
    return false;
  }

  // 出荷完了以降項目表示条件
  function showsShippingColumns() {
    // 部品手配中、出荷完了の場合表示
    // 部品手配中以降でも注文変更可能となるので、部品手配中以降での否認、停止、変更時も表示
    if (
      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
      inpOrder.statusClassValue === OrderStatus.COMPLETED ||
      (inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE &&
        inpOrder.replyDateTime) ||
      (inpOrder.statusClassValue === OrderStatus.CHANGED &&
        inpOrder.replyDateTime) ||
      (inpOrder.statusClassValue === OrderStatus.DENIED_CHANGE &&
        inpOrder.replyDateTime)
    ) {
      return true;
    }
    // その他は非表示
    return false;
  }

  // 出荷ボタン表示条件
  function showsShippingCheckColumn() {
    return showsShippingColumns();
  }

  // R3関連項目表示条件
  function showsR3Values() {
    // 顧客ユーザは常に非表示
    if (!auth.user().bemacFlg) {
      return false;
    }
    return showsShippingColumns();
  }

  // キャンセル列表示条件
  function showsCancelColumns() {
    // 顧客はキャンセルがなかったら非表示
    if (!auth.user().bemacFlg && getCancelTotalPrice() === 0) {
      return false;
    }

    return showsShippingColumns();
  }

  return {
    editableFileUpload,
    editableShippingFileUpload,
    editableUserComment,
    showsUserCommentCancel,
    showsUserCommentDeniedChange,
    editableBemacReplyComment,
    editableBemacFreeComment,
    editableBemacShippingComment,
    canAddNewRow,
    canChangeComplete,
    canDragRow,
    isGeneralEngineer,
    showsTempPrice,
    showsChangeColor,
    editableColumn,
    editableReplyColumn,
    editableR3Values,
    visibleReplyDeliveryDate,
    editableReplyDeliveryDate,
    canDeleteRow,
    showsReplyColumns,
    editableCancelQuantity,
    showsShippingColumns,
    showsShippingCheckColumn,
    showsR3Values,
    showsCancelColumns,
  };
}
